/**
 * Ficha de una cuenta, en ventana.  (061 · FR-003 · 060)
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { api, ErrorApi } from '../../lib/api'
import {
    cartelNombre,
    estadoDe,
    RejillaNegocios,
    SelectorAcceso,
    type NegocioDir,
    type UsuarioDir,
} from './usuario-piezas'

export function CuerpoUsuarioFicha({
    usuario: u,
    negocios,
    onCerrar,
}: {
    usuario: UsuarioDir
    negocios: NegocioDir[]
    onCerrar: () => void
}) {
    const qc = useQueryClient()
    const [nombre, setNombre] = useState(u.nombre)
    const [soloLectura, setSoloLectura] = useState(u.soloLectura)
    const [elegidos, setElegidos] = useState<number[]>([...u.accountIds])
    const [aviso, setAviso] = useState<string | null>(null)
    const [enlace, setEnlace] = useState<string | null>(null)
    const [confirmarBorrar, setConfirmarBorrar] = useState('')

    useEffect(() => {
        setNombre(u.nombre)
        setSoloLectura(u.soloLectura)
        setElegidos([...u.accountIds])
        setAviso(null)
        setEnlace(null)
        setConfirmarBorrar('')
    }, [u])

    const guardar = useMutation({
        mutationFn: () =>
            api.put(`/usuarios/${u.id}`, {
                accountIds: elegidos,
                soloLectura,
                nombre,
            }),
        onSuccess: () => {
            setAviso('Guardado.')
            setEnlace(null)
            void qc.invalidateQueries({ queryKey: ['usuarios'] })
        },
        onError: (e: Error) => {
            setAviso(e instanceof ErrorApi ? e.message : 'No se pudo guardar.')
        },
    })

    const enlaceClave = useMutation({
        mutationFn: () =>
            api.post<{ url: string; correoEnviado: boolean; kind: 'invite' | 'reset' }>(
                `/usuarios/${u.id}/enlace-clave`,
                {},
            ),
        onSuccess: (r) => {
            setAviso(
                r.correoEnviado
                    ? r.kind === 'reset'
                        ? 'Enlace de restablecimiento enviado.'
                        : 'Invitación reenviada.'
                    : 'El correo no salió: copia el enlace.',
            )
            setEnlace(r.url)
        },
        onError: (e: Error) => {
            setAviso(e instanceof ErrorApi ? e.message : 'No se pudo enviar el enlace.')
        },
    })

    const revocar = useMutation({
        mutationFn: () => api.post(`/usuarios/${u.id}/revoke`, {}),
        onSuccess: () => {
            setAviso('Deshabilitado. Ya no entra.')
            setEnlace(null)
            void qc.invalidateQueries({ queryKey: ['usuarios'] })
        },
        onError: (e: Error) => {
            setAviso(e instanceof ErrorApi ? e.message : 'No se pudo deshabilitar.')
        },
    })

    const reactivar = useMutation({
        mutationFn: () => api.post(`/usuarios/${u.id}/reactivar`, {}),
        onSuccess: () => {
            setAviso('Reactivado.')
            void qc.invalidateQueries({ queryKey: ['usuarios'] })
        },
        onError: (e: Error) => {
            setAviso(e instanceof ErrorApi ? e.message : 'No se pudo reactivar.')
        },
    })

    const borrar = useMutation({
        mutationFn: () => api.delete(`/usuarios/${u.id}`),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ['usuarios'] })
            onCerrar()
        },
        onError: (e: Error) => {
            setAviso(e instanceof ErrorApi ? e.message : 'No se pudo eliminar.')
        },
    })

    const est = estadoDe(u)
    const ocupado =
        guardar.isPending ||
        enlaceClave.isPending ||
        revocar.isPending ||
        reactivar.isPending ||
        borrar.isPending
    const puedeGuardar = nombre.trim().length > 0 && elegidos.length > 0

    return (
        <div className="space-y-6" data-testid="usuario-ficha">
            <header className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={est.tone}>{est.texto}</Badge>
                    {u.soloLectura ? (
                        <Badge tone="warning">Solo lectura</Badge>
                    ) : (
                        <Badge tone="success">Puede escribir</Badge>
                    )}
                </div>
                <p className="text-sm text-neutral-400">{u.email}</p>
            </header>

            {aviso && (
                <p role="status" className="text-sm text-neutral-300">
                    {aviso}
                </p>
            )}
            {enlace && (
                <p className="break-all text-xs text-sky-400">
                    <a href={enlace} className="underline">
                        {enlace}
                    </a>
                </p>
            )}

            <form
                className="space-y-6"
                onSubmit={(e) => {
                    e.preventDefault()
                    if (!puedeGuardar) return
                    guardar.mutate()
                }}
            >
                <div className="space-y-1.5">
                    <Label htmlFor="nombre-ficha">Nombre</Label>
                    <Input
                        id="nombre-ficha"
                        value={nombre}
                        onChange={(e) => setNombre(e.target.value)}
                    />
                </div>

                <div className="space-y-2">
                    <p className="text-xs font-medium text-neutral-400">Cómo entra</p>
                    <SelectorAcceso soloLectura={soloLectura} onChange={setSoloLectura} />
                </div>

                <div className="space-y-2">
                    <p className="text-xs font-medium text-neutral-400">Negocios</p>
                    <RejillaNegocios
                        negocios={negocios}
                        elegidos={elegidos}
                        onToggle={(id) =>
                            setElegidos((prev) =>
                                prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
                            )
                        }
                    />
                </div>

                <Button type="submit" disabled={ocupado || !puedeGuardar}>
                    {guardar.isPending ? 'Guardando…' : 'Guardar'}
                </Button>
            </form>

            <section className="space-y-3 border-t border-neutral-800 pt-6">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
                    Acceso a la cuenta
                </h3>
                <div className="flex flex-wrap gap-2">
                    {u.status === 'active' && (
                        <Button
                            type="button"
                            variant="outline"
                            disabled={ocupado}
                            onClick={() => enlaceClave.mutate()}
                        >
                            {u.tieneClave ? 'Enviar restablecimiento' : 'Reenviar invitación'}
                        </Button>
                    )}
                    {u.status === 'active' ? (
                        <Button
                            type="button"
                            variant="outline"
                            disabled={ocupado}
                            onClick={() => revocar.mutate()}
                        >
                            Deshabilitar
                        </Button>
                    ) : (
                        <Button
                            type="button"
                            variant="outline"
                            disabled={ocupado}
                            onClick={() => reactivar.mutate()}
                        >
                            Reactivar
                        </Button>
                    )}
                </div>
            </section>

            <section className="space-y-3 rounded-lg border border-rose-950 p-4">
                <h3 className="text-sm font-medium text-rose-300">Eliminar</h3>
                <p className="text-xs text-neutral-500">
                    El correo queda libre. Escribe{' '}
                    <span className="font-mono text-neutral-300">{u.email}</span> para
                    confirmar.
                </p>
                <Input
                    aria-label="Escribe el correo para eliminar"
                    value={confirmarBorrar}
                    onChange={(e) => setConfirmarBorrar(e.target.value)}
                    autoComplete="off"
                />
                <Button
                    type="button"
                    variant="outline"
                    className="border-rose-800 text-rose-300"
                    disabled={ocupado || confirmarBorrar.trim().toLowerCase() !== u.email}
                    onClick={() => borrar.mutate()}
                >
                    Eliminar cuenta
                </Button>
            </section>
        </div>
    )
}
